import type { z } from "zod";
import { createHash, randomBytes } from "node:crypto";
import { mkdir, readFile, rename, unlink, writeFile } from "node:fs/promises";
import { homedir } from "node:os";
import { dirname, join } from "node:path";
import type { Logger } from "../utils/logger.js";

export function sha1(input: string): string {
  return createHash("sha1").update(input).digest("hex");
}

export async function ensureDir(path: string): Promise<void> {
  await mkdir(path, { recursive: true });
}

export async function atomicWriteJson(path: string, value: unknown): Promise<void> {
  await ensureDir(dirname(path));
  const tmp = `${path}.${process.pid}.${Date.now()}.${randomBytes(6).toString("hex")}.tmp`;
  const body = JSON.stringify(value);
  await writeFile(tmp, body, "utf8");
  try {
    await rename(tmp, path);
  } catch (err) {
    try {
      await unlink(tmp);
    } catch {
      // ignore cleanup failure
    }
    throw err;
  }
}

export async function readJsonWithSchema<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  opts: { logger?: Logger } = {},
): Promise<z.infer<S> | null> {
  let text: string;
  try {
    text = await readFile(path, "utf8");
  } catch (err) {
    if (isEnoent(err)) return null;
    throw err;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    opts.logger?.warn({ path, err }, "cache file is not valid JSON; treating as miss");
    return null;
  }
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    opts.logger?.warn({ path, issues: parsed.error.issues }, "cache file failed schema; treating as miss");
    return null;
  }
  return parsed.data;
}

export function userCacheDir(): string {
  if (process.platform === "win32") {
    return process.env.LOCALAPPDATA ?? join(homedir(), "AppData", "Local");
  }
  if (process.platform === "darwin") {
    return join(homedir(), "Library", "Caches");
  }
  return process.env.XDG_CACHE_HOME ?? join(homedir(), ".cache");
}

export function isEnoent(err: unknown): boolean {
  return typeof err === "object" && err !== null && "code" in err && err.code === "ENOENT";
}
